import { get } from 'lodash';
import api from './api';

const participantService = {
  addParticipant: async (studyID, siteID, participant) => {
    try {
      const response = await api.post(`/api/studies/sites/${siteID}/add-participant`, participant);
      return response.data;
    } catch (error) {
      const payload = error?.response?.data;

      // The API intentionally keeps the participant when survey-window
      // generation is temporarily incomplete. Treat that response as a
      // successful enrollment with a warning; retrying the form can otherwise
      // create a confusing duplicate-participant failure.
      if (
        error?.response?.status === 422 &&
        payload?.code === 'generation_incomplete' &&
        payload?.id &&
        payload?.participant_id
      ) {
        return {
          ...payload,
          generation_warning:
            payload.error ||
            'Participant created; survey setup will retry automatically.',
        };
      }

      throw error;
    }
  },

  bulkCreateParticipants: async (studyId, payload) => {
    const response = await api.post(`/api/studies/${studyId}/bulk-create-participants/`, payload);
    return response.data;
  },
  
  // Function to get all assessments for a participant including historical data
  // Backend handles filtering of future assessments based on user permissions
  getAllAssessmentsForParticipant: async (participantID, bypassCache = true, includeResponses = false, showFutureAssessments = false, simulatedDate = null) => {
    try {
      // The backend will automatically filter future assessments based on user permissions
      // Internal users can see future assessments, regular users cannot
      const response = await api.get(`/api/participants/${participantID}/all-assessments/`, {
        params: {
          bypass_cache: bypassCache,
          include_responses: includeResponses,
          show_future_assessments: showFutureAssessments,
          simulated_date: simulatedDate
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching all assessments for participant:', error);
      throw error;
    }
  },

  getParticipantsForSite: async (studyID, siteID) => {
    try {
      const response = await api.get(`/api/studies/${studyID}/sites/${siteID}/participants`);
      return response.data;
    } catch (error) {
      console.error('Error fetching participants:', error);
      throw error;
    }
  },

  getParticipant: async (participantID) => {
    try {
      // Add a timestamp for cache busting to ensure we always get fresh data
      const timestamp = new Date().getTime();
      const response = await api.get(`/api/participants/${participantID}?ts=${timestamp}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching participant details:', error);
      throw error;
    }
  },
  
  getAllParticipants: async () => {
    try {
      const response = await api.get('/api/participants/');
      return response.data;
    } catch (error) {
      logAxiosError(error);
      throw error;
    }
  },

  deactivateParticipant: async (participantID, deactivationDate) => {
    try {
      const response = await api.patch(`/api/participants/${participantID}/deactivate`, {
        deactivation_date: deactivationDate
      });
      return response.data;
    } catch (error) {
      console.error('Error deactivating participant:', error);
      throw error;
    }
  },

  activateParticipant: async (participantID, reactivationDate) => {
    try {
      const response = await api.patch(`/api/participants/${participantID}/activate`, {
        reactivation_date: reactivationDate
      });
      return response.data;
    } catch (error) {
      console.error('Error activating participant:', error);
      throw error;
    }
  },

  getParticipantLastDataSend: async (participantID) => {
    try {
      const response = await api.get(`/api/participants/${participantID}/last-data-send`);
      return response.data;
    } catch (error) {
      console.error('Error fetching participant last data send:', error);
      throw error;
    }
  },

  // FECOA-1609: poll window-generation status. Pass studyID and/or participantID
  // to scope the rollup. Returns { aggregate, participants: [{ participant_id,
  // status, ready_count, total_count, error, completed_at }] }.
  getGenerationStatus: async ({ studyID, participantID } = {}) => {
    try {
      const params = {};
      if (studyID) params.study_id = studyID;
      if (participantID) params.participant_id = participantID;
      const response = await api.get('/api/participant-schedules/generation-status/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching window generation status:', error);
      throw error;
    }
  },

  // FECOA-1609: coordinator action to clear a stuck/failed generation state.
  // Cosmetic flip to 'complete'; does not cancel a running task.
  markGenerationComplete: async (participantID) => {
    const response = await api.post('/api/participant-schedules/mark-complete/', {
      participant_id: participantID,
    });
    return response.data;
  },

  checkMissedSurveys: async (participantID) => {
    try {
      console.log(`Checking missed surveys for participant: ${participantID}`);
      const url = `/api/participants/${participantID}/check-missed-surveys/`;
      console.log(`API URL: ${url}`);
      
      const response = await api.post(url);
      console.log('Check missed surveys response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error checking missed surveys:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      console.error('Status code:', error.response ? error.response.status : 'Unknown');
      
      // Don't throw the error as this is a background check that shouldn't break the UI
      return { 
        error: error.message,
        details: error.response ? error.response.data : null,
        status: error.response ? error.response.status : null
      };
    }
  }
};

export default participantService;
